'use client';
import { GTag, setGA4Consent } from '@ifixit/analytics';
import { useTranslations } from '@ifixit/i18n/hooks';
import { Button } from '@ifixit/ui/components/button';
import { cn } from '@ifixit/ui/lib/utils';
import {
   FunctionComponent,
   PropsWithChildren,
   ReactNode,
   useCallback,
   useEffect,
   useState,
} from 'react';
import { useStableCallback } from '@ifixit/ui/hooks/use-stable-callback';
import { ShopifyConfig, updateShopifyConsent, useSyncShopifyConsent } from './shopify-consent';
import { ConsentStatus, NOT_SEEN, usePiwikConsent } from './use-piwik-consent';

const CONSENT_BANNER_OPEN_EVENT = 'consent-banner:open';

declare global {
   interface Window {
      gtag?: GTag;
   }
}

export interface CustomLinkProps {
   href?: string;
   className?: string;
   children?: ReactNode;
}

export function ConsentBanner({
   CustomLink,
   privacyPolicyUrl,
   needsConsent,
   piwikSiteId,
   shopifyConfig,
}: {
   CustomLink: FunctionComponent<PropsWithChildren<CustomLinkProps>>;
   privacyPolicyUrl: string;
   needsConsent: boolean;
   piwikSiteId: string;
   shopifyConfig: ShopifyConfig;
}) {
   const t = useTranslations('ConsentBanner');
   const [manuallyOpened, setManuallyOpened] = useState(false);
   const [showDetails, setShowDetails] = useState(false);

   const consent = useConsent({ shopifyConfig, needsConsent });

   useSyncShopifyConsent({
      piwikSiteId,
      shopifyConfig,
      needsConsent,
   });

   useOpenBannerRequestEvent(options => {
      setManuallyOpened(true);
      if (options.showDetails) setShowDetails(true);
   });

   const closeBanner = () => {
      setShowDetails(false);
      setManuallyOpened(false);
   };

   const showBanner =
      consent.isReady && (manuallyOpened || (needsConsent && consent.status === NOT_SEEN));

   return (
      <>
         <script
            async
            src="https://cdn.shopify.com/shopifycloud/consent-tracking-api/v0.1/consent-tracking-api.js"
         />
         {showBanner && (
            <div
               data-testid="consent-banner"
               className={cn(
                  'pointer-events-auto fixed bottom-6 left-1/2 z-50 w-[calc(100vw-38px)]',
                  'max-w-[944px] -translate-x-1/2 text-base sm:bottom-3'
               )}
            >
               <div
                  className={cn('mx-auto rounded-lg border border-gray-300 bg-white p-6 shadow-lg')}
               >
                  <div
                     className={cn(
                        'mx-auto flex flex-col items-start gap-2',
                        'sm:flex-row sm:items-center sm:gap-3'
                     )}
                  >
                     <p className="m-0 text-inherit">
                        {t.rich('intro', {
                           link: (chunks: ReactNode) => (
                              <CustomLink href={privacyPolicyUrl}>{chunks}</CustomLink>
                           ),
                        })}
                     </p>
                     {!showDetails && (
                        <div className="flex gap-2 sm:gap-3">
                           <Button
                              className="button-secondary button-xs sm:button-sm"
                              onClick={() => setShowDetails(true)}
                              data-testid="details-button"
                           >
                              {t('buttons.details')}
                           </Button>
                           <Button
                              className="button-primary button-xs sm:button-sm"
                              onClick={() => consent.acceptAll().then(closeBanner)}
                              data-testid="accept-all-button"
                           >
                              {t('buttons.acceptAll')}
                           </Button>
                        </div>
                     )}
                  </div>
                  {showDetails && (
                     <Details
                        acceptAll={() => consent.acceptAll().then(closeBanner)}
                        rejectAll={() => consent.rejectAll().then(closeBanner)}
                        CustomLink={CustomLink}
                     />
                  )}
               </div>
            </div>
         )}
      </>
   );
}

function Details({
   acceptAll,
   rejectAll,
   CustomLink,
}: {
   acceptAll: () => void;
   rejectAll: () => void;
   CustomLink: FunctionComponent<PropsWithChildren<CustomLinkProps>>;
}) {
   const t = useTranslations('ConsentBanner');
   return (
      <div data-testid="details" className="mt-2 sm:mt-3">
         <div className="flex gap-2 sm:gap-3">
            <Button
               className="button-secondary button-xs sm:button-sm"
               onClick={rejectAll}
               data-testid="reject-all-button"
            >
               {t('buttons.rejectAll')}
            </Button>
            <Button
               className="button-primary button-xs sm:button-sm"
               onClick={acceptAll}
               data-testid="accept-all-button"
            >
               {t('buttons.acceptAll')}
            </Button>
         </div>
         <div className="mt-3 flex flex-col gap-2 sm:mt-6">
            <p className="m-0">{t('details.heading')}</p>
            <ul className="m-0 mt-1 list-disc pl-6">
               <li>{t('details.firstBullet')}</li>
               <li>{t('details.secondBullet')}</li>
               <li>{t('details.thirdBullet')}</li>
            </ul>
            <p className="m-0">
               {t.rich('details.subtext', {
                  link: (chunks: ReactNode) => (
                     <CustomLink href="https://business.safety.google/privacy/">
                        {chunks}
                     </CustomLink>
                  ),
               })}
            </p>
         </div>
      </div>
   );
}

function useConsent({
   shopifyConfig,
   needsConsent,
}: {
   shopifyConfig: ShopifyConfig;
   needsConsent: boolean;
}): {
   isReady: boolean;
   status: ConsentStatus | null;
   acceptAll: () => Promise<void>;
   rejectAll: () => Promise<void>;
} {
   const piwik = usePiwikConsent();
   const { checkout, storefront, accessToken } = shopifyConfig;

   const acceptAll = useCallback(async () => {
      await piwik.acceptAll();
      setGA4Consent({ consent: true, type: 'update' });
      updateShopifyConsent(true, { checkout, storefront, accessToken });
   }, [piwik, checkout, storefront, accessToken]);

   const rejectAll = useCallback(async () => {
      await piwik.rejectAll();
      setGA4Consent({ consent: false, type: 'update' });
      updateShopifyConsent(false, { checkout, storefront, accessToken });
   }, [piwik, checkout, storefront, accessToken]);

   // Auto-accept for non-EU visitors
   useEffect(() => {
      if (piwik.isReady && !needsConsent && piwik.consentStatus === NOT_SEEN) void acceptAll();
   }, [piwik.isReady, piwik.consentStatus, needsConsent, acceptAll]);

   return {
      isReady: piwik.isReady,
      status: piwik.consentStatus,
      acceptAll,
      rejectAll,
   };
}

export interface OpenConsentBannerOptions {
   showDetails?: boolean;
}

export function openConsentBanner(options: OpenConsentBannerOptions = {}) {
   window.dispatchEvent(new CustomEvent(CONSENT_BANNER_OPEN_EVENT, { detail: options }));
}

function useOpenBannerRequestEvent(onOpen: (options: OpenConsentBannerOptions) => void) {
   const stableOnOpen = useStableCallback(onOpen);

   useEffect(() => {
      const handleOpen = (e: Event) => {
         stableOnOpen((e as CustomEvent).detail ?? {});
      };
      window.addEventListener(CONSENT_BANNER_OPEN_EVENT, handleOpen);
      return () => window.removeEventListener(CONSENT_BANNER_OPEN_EVENT, handleOpen);
   }, [stableOnOpen]);
}
